import React, { useState } from "react";
import { useSelector } from "react-redux";
import LanguageContext from "../../helper/LanguageContext";
import { callAPI } from "../../helper/apiUtils";
import { $crud } from "../../helper/CrudFactory";
import { BASE_URL } from "../../helper/constants";
import {
  InfoCaption,
  InfoCard,
  InfoHero,
  InfoMemberRow,
  InfoRow,
  InfoTiles,
  infoText,
  myFullName,
  useChatMediaCount,
} from "./info/ChatInfoParts";
import { openInTeams } from "../../utils/teams/teamsChat";
import TeamsChatRoomModal from "./TeamsChatRoomModal";
import "./TeamsChat.css";

const avatarSrc = (p) => (typeof p === "string" && p.trim() ? (/^(https?:|blob:|data:)/i.test(p) ? p : BASE_URL + p) : null);

/**
 * Chat info of a Microsoft Teams chat (mobile GroupInfo with its Teams flags): read-only. Members,
 * name, description and leaving are managed in Teams, so the page offers search, mute, media and
 * "Open in Teams" plus the member list (the Teams people are not Kjuup users: no profile, no
 * direct chat, no presence).
 *
 *   chat      the open-chat entry (props.chat of the pane)
 *   teams     teamsChatFrom(...) of the pane: { chatType, webUrl, ... }
 *   members   the chat's users from /group/:id/info without me (Teams people included)
 *   project   the project room the chat is filed under ({ id, name }) or null
 *   onProjectChange  (project | null) after the room was assigned or removed; omit to hide the row
 */
export default function TeamsChatInfo({ chat, teams, title, members = [], isInternal = false, project = null, onProjectChange, onClose, onSearch, onMedia }) {
  const { isGerm, strings } = React.useContext(LanguageContext);
  const t = (key, de, en) => infoText(strings, isGerm, key, de, en);
  const me = useSelector((s) => s.profile) || {};
  const [muted, setMuted] = useState(!!(chat && (chat.isMute || chat.group?.isMute)));
  const [muteBusy, setMuteBusy] = useState(false);
  const [roomOpen, setRoomOpen] = useState(false);
  const mediaCount = useChatMediaCount(chat?.id, false);
  const oneOnOne = teams && teams.chatType === "oneOnOne";

  const toggleMute = async () => {
    if (muteBusy || !chat?.id) return;
    const next = !muted;
    setMuteBusy(true);
    const res = await callAPI("/group/muteUnmuteChat", { mute: next, group: chat.id }, "PUT");
    setMuteBusy(false);
    const data = res && res.data;
    if (data && data.status) {
      setMuted(next);
      $crud.notify({ type: "success", message: next ? strings.chat_muted_successfully : strings.chat_unmuted_successfully });
    } else {
      $crud.notify({ type: "error", message: (data && data.message) || strings.something_went_wrong });
    }
  };

  const tiles = [
    onSearch ? { key: "search", icon: "search", label: strings.search || (isGerm ? "Suche" : "Search"), onClick: onSearch } : null,
    {
      key: "mute",
      icon: muted ? "notifications" : "notifications_off",
      label: muted ? t("chat_info_unmute", "Ton an", "Unmute") : t("chat_info_mute", "Stumm", "Mute"),
      ariaLabel: muted ? strings.unmute : strings.mute,
      onClick: toggleMute,
    },
    onMedia ? { key: "media", icon: "perm_media", label: t("chat_info_media", "Dateien", "Files"), onClick: onMedia } : null,
    teams && teams.webUrl
      ? { key: "teams", icon: "open_in_new", label: strings.teams_title || "Teams", ariaLabel: strings.teams_open_in_teams || "Open in Teams", onClick: () => openInTeams(teams.webUrl) }
      : null,
  ];

  const rows = [
    { key: "me", name: myFullName(me) || strings.me || (isGerm ? "Ich" : "Me"), src: avatarSrc(me.avatar), roleLine: "" },
    ...(Array.isArray(members) ? members : [])
      .filter((u) => u && !u.isLeave)
      .map((u) => ({
        key: String(u.id),
        name: String(u.fullName || [u.firstName, u.lastName].filter(Boolean).join(" ") || strings.teams_user_fallback || "Teams user").trim(),
        src: avatarSrc(u.avatar),
        roleLine: u.isTeamsMember ? "Microsoft Teams" : "",
        externalLabel: u.isExternal ? (strings.external || (isGerm ? "Extern" : "External")) : null,
      })),
  ];

  return (
    <div className={`chat_user_info group_chat_user_info border-radius-0 m-0 p-0 user-info kjp-info-shell${isInternal ? " chat_user_info-internal user-info--internal" : ""}`}>
      <div className="kjp-info-head">
        <button
          type="button"
          className="kj-btn kj-btn--icon kj-btn--neutral kjp-info-head__back"
          onClick={onClose}
          aria-label={strings.back || (isGerm ? "Zurück" : "Back")}
          title={strings.back || (isGerm ? "Zurück" : "Back")}
        >
          <span className="material-symbols-outlined">keyboard_arrow_left</span>
        </button>
        <div className="kjp-info-head__title">{t("chat_info_title_chat", "Chat-Info", "Chat info")}</div>
      </div>
      <div className="kjp-info-shell__body teams-chat-info__body">
        <div className="kjp-info">
          <InfoHero kind={oneOnOne ? "person" : "group"} name={title} src={null} sub={teams && teams.accountEmail ? `Microsoft Teams · ${teams.accountEmail}` : "Microsoft Teams"} />
          <InfoTiles tiles={tiles} />
          <InfoCard>
            {teams && teams.webUrl ? (
              <InfoRow key="teams" icon="open_in_new" label={strings.teams_open_in_teams || "Open in Teams"} onClick={() => openInTeams(teams.webUrl)} />
            ) : null}
            {onProjectChange ? (
              <InfoRow
                key="room"
                icon="folder_special"
                label={strings.teams_room_label || (isGerm ? "Projekt" : "Project")}
                value={project && project.name ? project.name : strings.teams_room_none || (isGerm ? "Keine Zuordnung" : "Not assigned")}
                onClick={() => setRoomOpen(true)}
              />
            ) : null}
            {onMedia ? (
              <InfoRow
                key="media"
                icon="photo"
                label={strings.media_link_docs}
                value={mediaCount != null ? String(mediaCount) : null}
                onClick={onMedia}
              />
            ) : null}
          </InfoCard>
          <InfoCaption title={`${strings.teams_members || t("chat_info_members", "Mitglieder", "Members")} · ${rows.length}`} />
          <InfoCard>
            {rows.map((m) => (
              <InfoMemberRow key={m.key} m={m} />
            ))}
          </InfoCard>
          <p className="teams-chat-info__note">
            {strings.teams_managed_in_teams || "Members, name and leaving the chat are managed in Microsoft Teams."}
          </p>
        </div>
      </div>
      {roomOpen ? (
        <TeamsChatRoomModal
          groupId={chat?.id}
          project={project}
          onSaved={onProjectChange}
          onClose={() => setRoomOpen(false)}
        />
      ) : null}
    </div>
  );
}
